import React, { useState, useEffect } from 'react';
import { updateProfile, getUserProfile, fetchRecentSearches } from '../utils/api';
import './AccountPage.css';

const GENDERS = ['Male', 'Female', 'Non-binary', 'Other'];
const TASTE_DIMS = ['Spice', 'Sour', 'Sweet', 'Bitter', 'Umami', 'Salt', 'Fatty'];
const AROMA_DIMS = ['Smoky', 'Herbal', 'Citrusy', 'Fermented', 'Roasted', 'Fresh', 'SweetAroma', 'Pungent', 'Earthy'];

/** Learned-preference bars: width = preference (0-1 → 1-10), opacity = certainty. */
function TasteProfileTab({ username }) {
  const [profile, setProfile] = useState(null);
  const [searches, setSearches] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    getUserProfile(username).then(setProfile).catch(e => setError(e.message));
    fetchRecentSearches(username)
      .then(res => setSearches((res.searches || res || []).slice(0, 10)))
      .catch(() => {});
  }, [username]);

  if (error) return <p className="account-hint">Could not load taste profile: {error}</p>;
  if (!profile) return <p className="account-hint">Loading taste profile…</p>;

  const learned = profile.learned_pref || {};
  const certainty = profile.certainty || {};
  const counts = profile.history_counts || {};

  const renderBars = (dims, cls) => dims.map(dim => {
    const v = learned[dim] ?? 0.5;
    const c = certainty[dim] ?? 0.5;
    return (
      <div className="tp-row" key={dim}>
        <span className="tp-label">{dim}</span>
        <div className="tp-track">
          <div className={`tp-bar ${cls}`}
               style={{ width: `${Math.round(v * 100)}%`, opacity: 0.35 + 0.65 * c }} />
        </div>
        <span className="tp-value">{(v * 9 + 1).toFixed(1)}</span>
      </div>
    );
  });

  return (
    <div>
      <section className="account-section">
        <h3>Learned taste preferences</h3>
        <p className="account-hint">
          Bar length = preference strength (1–10). Solidity = how certain the
          model is, based on your feedback so far.
        </p>
        {renderBars(TASTE_DIMS, 'taste')}
        <h3 style={{ marginTop: 16 }}>Learned aroma preferences</h3>
        {renderBars(AROMA_DIMS, 'aroma')}
      </section>

      <section className="account-section">
        <h3>Activity</h3>
        <div className="tp-counts">
          <div><strong>{counts.tried ?? 0}</strong><span>tried</span></div>
          <div><strong>{counts.favorited ?? 0}</strong><span>favorited</span></div>
          <div><strong>{counts.rejected ?? 0}</strong><span>rejected</span></div>
          <div><strong>{counts.feedback_entries ?? 0}</strong><span>feedback</span></div>
        </div>
      </section>

      <section className="account-section">
        <h3>Recent searches</h3>
        {searches.length === 0 && <p className="account-hint">No searches yet.</p>}
        {searches.map((s, i) => {
          let foods = [];
          try {
            const parsed = typeof s.results === 'string' ? JSON.parse(s.results) : s.results;
            foods = (parsed || []).map(r => (typeof r === 'string' ? r : r['Food Item'] || r.food_name)).filter(Boolean);
          } catch { /* ignore */ }
          return (
            <div className="tp-search" key={s.id || i}>
              <span className="tp-search-date">{(s.timestamp || '').slice(0, 16).replace('T', ' ')}</span>
              <span className="tp-search-foods">{foods.slice(0, 3).join(', ') || s.mode || 'search'}</span>
            </div>
          );
        })}
      </section>
    </div>
  );
}

/**
 * Edit-profile page.
 *  - Name + gender save directly (no reauth).
 *  - Email and password changes open a modal asking for the current password;
 *    the backend independently verifies it before applying.
 */
function AccountPage({ currentUser, onUserUpdated, onClose }) {
  const [tab, setTab] = useState('account'); // 'account' | 'taste'
  const [firstName, setFirstName] = useState(currentUser.firstName || '');
  const [lastName, setLastName] = useState(currentUser.lastName || '');
  const [gender, setGender] = useState(currentUser.gender || '');
  const [email, setEmail] = useState(currentUser.email || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [status, setStatus] = useState(null); // {kind: 'ok'|'err', text}
  // reauth modal state: {updates, label} | null
  const [reauth, setReauth] = useState(null);
  const [reauthPassword, setReauthPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const flash = (kind, text) => {
    setStatus({ kind, text });
    setTimeout(() => setStatus(null), 4000);
  };

  const applyUpdate = async (updates, currentPassword = '') => {
    setBusy(true);
    try {
      const res = await updateProfile(currentUser.username, updates, currentPassword);
      onUserUpdated(res.user);
      flash('ok', 'Saved ✓');
      return true;
    } catch (e) {
      flash('err', e.message || 'Update failed');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveBasics = () => {
    applyUpdate({ firstName, lastName, gender });
  };

  const requestEmailChange = () => {
    if (!email.trim()) { flash('err', 'Email cannot be empty'); return; }
    setReauth({ updates: { email: email.trim() }, label: 'change your email' });
  };

  const requestPasswordChange = () => {
    if (!newPassword) { flash('err', 'New password cannot be empty'); return; }
    if (newPassword !== confirmPassword) { flash('err', 'Passwords do not match'); return; }
    setReauth({ updates: { password: newPassword }, label: 'change your password' });
  };

  const confirmReauth = async () => {
    const ok = await applyUpdate(reauth.updates, reauthPassword);
    if (ok) {
      setReauth(null);
      setReauthPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  return (
    <div className="account-overlay">
      <div className="account-card">
        <button className="account-close" onClick={onClose}>×</button>
        <h2>Account Settings</h2>
        <p className="account-username">@{currentUser.username}</p>

        <div className="account-tabs">
          <button className={tab === 'account' ? 'active' : ''}
                  onClick={() => setTab('account')}>Account</button>
          <button className={tab === 'taste' ? 'active' : ''}
                  onClick={() => setTab('taste')}>Taste Profile</button>
        </div>

        {status && (
          <div className={`account-status ${status.kind}`}>{status.text}</div>
        )}

        {tab === 'taste' && <TasteProfileTab username={currentUser.username} />}

        {tab === 'account' && (<>
        <section className="account-section">
          <h3>Profile</h3>
          <label>First name
            <input value={firstName} onChange={e => setFirstName(e.target.value)} />
          </label>
          <label>Last name
            <input value={lastName} onChange={e => setLastName(e.target.value)} />
          </label>
          <label>Gender
            <select value={gender} onChange={e => setGender(e.target.value)}>
              <option value="">Prefer not to say</option>
              {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          <button className="account-save" disabled={busy} onClick={saveBasics}>
            Save profile
          </button>
        </section>

        <section className="account-section">
          <h3>Email</h3>
          <label>Email address
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                   placeholder="you@example.com" />
          </label>
          <button className="account-save" disabled={busy} onClick={requestEmailChange}>
            Update email…
          </button>
          <p className="account-hint">Requires your current password.</p>
        </section>

        <section className="account-section">
          <h3>Password</h3>
          <label>New password
            <input type="password" value={newPassword}
                   onChange={e => setNewPassword(e.target.value)} />
          </label>
          <label>Confirm new password
            <input type="password" value={confirmPassword}
                   onChange={e => setConfirmPassword(e.target.value)} />
          </label>
          <button className="account-save" disabled={busy} onClick={requestPasswordChange}>
            Update password…
          </button>
          <p className="account-hint">Requires your current password.</p>
        </section>
        </>)}
      </div>

      {reauth && (
        <div className="reauth-overlay">
          <div className="reauth-card">
            <h3>Confirm it's you</h3>
            <p>Enter your current password to {reauth.label}.</p>
            <input
              type="password"
              autoFocus
              placeholder="Current password"
              value={reauthPassword}
              onChange={e => setReauthPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && confirmReauth()}
            />
            <div className="reauth-actions">
              <button className="reauth-cancel"
                      onClick={() => { setReauth(null); setReauthPassword(''); }}>
                Cancel
              </button>
              <button className="reauth-confirm" disabled={busy || !reauthPassword}
                      onClick={confirmReauth}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountPage;
